/* eslint-disable no-unused-vars */
import { FunctionComponent, useState, useEffect } from "react";
import LoginStyles from "./login.module.css";
import { Button } from "primereact/button";
import Logo from "assets/adroit_logo.png";
import { IUser } from "types/user.type";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { setToaster } from "common/toastStore";
import AuthService from "services/AuthService";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "common/Loader";

interface LoginProps {}

interface ResetForm extends IUser {
  confirmPassword: string;
}

const ResetPassword: FunctionComponent<LoginProps> = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordShownConfirm, setPasswordShownConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(10);

  const togglePassword = () => setPasswordShown(!passwordShown);
  const togglePasswordConfirm = () =>
    setPasswordShownConfirm(!passwordShownConfirm);

  /** ✅ Yup Validation Schema */
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Enter a valid email address")
      .required("Email is required"),

    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Must contain at least one special character"
      ),

    confirmPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  };

  const { register, handleSubmit, formState } = useForm<ResetForm>(
    formOptions as object
  );
  const { errors, isValid } = formState;

  const onSubmit = (data: ResetForm) => {
    if (!isValid) return;
    setLoading(true);

    AuthService.resetPassword(data.email, data.password, token)
      .then((_response: any) => {
        setSuccess(true);
        setToaster({
          severity: "success",
          summary: "Success",
          detail: "Password updated successfully!",
        });
      })
      .catch((error: { response: { data: any[] } }) => {
        setToaster({
          severity: "error",
          summary: "Error Message",
          detail: error?.response?.data?.[0] || "Something went wrong",
        });
      })
      .finally(() => setLoading(false));
  };

  /** 🔁 Auto redirect after success */
  useEffect(() => {
    if (success) {
      const interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      const timeout = setTimeout(() => {
        navigate("/login", { replace: true });
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [success, navigate]);

  return (
    <>
      <div className={LoginStyles.login_bg}>
        {!success ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-6 flex justify-center">
              <img src={Logo} alt="Adroit Logo" />
            </div>

            <div className="bg-white w-96 rounded-2xl p-6 shadow-lg">
              <p className="text-center text-md font-medium py-1">
                Reset your password
              </p>

              {/* Email Field */}
              <div className="my-8 relative z-0 w-full ">
                <div className="p-float-label">
                  <InputText
                    id="email"
                    {...register("email")}
                    className="w-full text-sm"
                  />
                  <label htmlFor="email">Enter Email ID</label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.email?.message}
                </div>
              </div>

              {/* Password Field */}
              <div className="my-6 relative z-0 w-full">
                <div className="p-float-label p-input-icon-right w-full">
                  <i
                    className={`pi cursor-pointer ${
                      passwordShown ? "pi-eye" : "pi-eye-slash"
                    }`}
                    onClick={togglePassword}
                  />
                  <InputText
                    id="password"
                    {...register("password")}
                    type={passwordShown ? "text" : "password"}
                    className="w-full text-sm"
                  />
                  <label htmlFor="password">Enter Password</label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.password?.message}
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="my-6 relative z-0 w-full">
                <div className="p-float-label p-input-icon-right w-full">
                  <i
                    className={`pi cursor-pointer ${
                      passwordShownConfirm ? "pi-eye" : "pi-eye-slash"
                    }`}
                    onClick={togglePasswordConfirm}
                  />
                  <InputText
                    id="confirmPassword"
                    {...register("confirmPassword")}
                    type={passwordShownConfirm ? "text" : "password"}
                    className="w-full text-sm"
                  />
                  <label htmlFor="confirmPassword">Confirm Password</label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.confirmPassword?.message}
                </div>
              </div>

              <div>
                <Button
                  label="Submit"
                  type="submit"
                  className={LoginStyles.login_submit}
                />
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-white w-96 rounded-2xl p-6 shadow-lg text-center">
            <h2 className="text-lg font-semibold mb-4 text-green-700">
              Password reset successful!
            </h2>
            <p className="text-sm mb-2">
              You will be redirected to the login page in{" "}
              <strong>{timer}</strong> seconds.
            </p>
            <p className="text-sm">
              Or{" "}
              <span
                className="text-blue-600 underline cursor-pointer"
                onClick={() => navigate("/login")}
              >
                click here
              </span>{" "}
              to go to the login page now.
            </p>
          </div>
        )}
      </div>

      {loading && <Loader />}
    </>
  );
};

export default ResetPassword;
